import { types as api } from '@mesa-labs/mesa-api';
import { Dropdown, LoadingSpinner, StatusColors } from '@mesa-labs/mesa-ui';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { useGetVendorActionItemTimelineQuery } from '../../../../redux/api/vendors';
import { ActionItemActions } from '../hooks';
import ActionItemContext from './ActionItemContext';
import ActionItemTimeline from './ActionItemTimeline';
import Linkified from './Linkified';
import InlineText from './InlineText';
import {
  ErrorText, Muted, Segment, Segmented,
} from './styles';
import VendorEyebrow from './VendorEyebrow';
import VendorSummary from './VendorSummary';
import { PartnerChips, PartnerProgramList } from './PartnerTag';
import { formatDateTime } from '../../../../utils';
import {
  ActionItemDetailView, ActionItemDetailViewOptions, ActionItemStatusOptions, actionItemStatusColorKey, assigneeOptions, formatAssignee, formatAssigneeEmail,
  timelineView,
} from '../../../../utils/vendorActionItems';

const Detail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

// Right padding keeps the title (and its edit box) clear of the drawer's close button.
const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-right: 12px;
`;

// Vendor link with its partner chips; wraps under a long Vendor name.
const HeaderContext = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
`;

const DetailTitle = styled.div<{ $canceled: boolean }>`
  color: #101828;
  font-size: 16px;
  font-weight: 600;
  ${(p) => p.$canceled && 'text-decoration: line-through;'}
`;

const SectionLabel = styled.div`
  color: ${(p) => p.theme.colors.Gray700};
  font-size: 12px;
  font-weight: 600;
`;

const DetailsBox = styled.div`
  align-items: center;
  border: 1px solid #EAECF0;
  border-radius: 8px;
  color: ${(p) => p.theme.colors.Gray700};
  display: grid;
  font-size: 12px;
  gap: 4px 12px;
  grid-template-columns: 90px 1fr;
  padding: 10px 12px;
`;

// Statuses in lifecycle order, so the control also shows where the item is in it.
// The lifecycle statuses on the left; Canceled set apart on the right so a slip past Done doesn't cancel.
const StatusRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: space-between;
  min-width: 0;
`;

const StatusSegmented = styled(Segmented)`
  & > button { white-space: nowrap; }
`;

const LifecycleStatusOptions = ActionItemStatusOptions.filter(({ value }) => value !== api.VendorActionItemStatus.CANCELED);
const CanceledStatusOptions = ActionItemStatusOptions.filter(({ value }) => value === api.VendorActionItemStatus.CANCELED);

// The picked status takes its status-label colors, as in the table view.
const StatusSegment = styled(Segment)<{ $colorKey: string }>`
  ${(p) => p.$on && `
    background: ${(StatusColors[p.$colorKey] ?? StatusColors.default).backgroundColor};
    color: ${(StatusColors[p.$colorKey] ?? StatusColors.default).color};
  `}
`;

// The Details dropdown menus match their field's width and show more options before scrolling than the 200px default.
const MenuMaxHeight = '360px';

const FieldLabel = styled.span`
  color: ${(p) => p.theme.colors.Gray500};
`;

const ReadOnlyValue = styled.span`
  color: #101828;
  padding: 4px 2px;
`;

// The empty value clears the assignee.
const UnassignedOption = { label: 'Unassigned', value: '' };

type HeaderProps = {
  item: api.VendorActionItemResponse;
  vendorName: string;
  canWrite: boolean;
  actions: ActionItemActions;
};

// The Vendor link and click-to-edit title, rendered as the drawer's title.
export function ActionItemDetailHeader({
  item, vendorName, canWrite, actions,
}: HeaderProps): React.ReactElement {
  return (
    <Header>
      <HeaderContext>
        <VendorEyebrow to={`/vendors/${item.vendorId}`} style={{ fontSize: 13 }}>{vendorName}</VendorEyebrow>
        <PartnerChips item={item} />
      </HeaderContext>
      <InlineText
        key={`title-${item.id}`}
        value={item.title}
        placeholder="Add a title"
        required
        readOnly={!canWrite}
        display={(v) => <DetailTitle $canceled={item.status === api.VendorActionItemStatus.CANCELED}>{v}</DetailTitle>}
        onSave={(title) => actions.update(item.id, { title })}
      />
    </Header>
  );
}

type Props = {
  item: api.VendorActionItemResponse;
  me?: string;
  operators: readonly api.VendorActionItemAssigneeResponse[];
  canWrite: boolean;
  actions: ActionItemActions;
  // Opens another of the item's Vendor's items in this drawer.
  onOpenItem: (id: string) => void;
};

export default function ActionItemDetail({
  item, me, operators, canWrite, actions, onOpenItem,
}: Props): React.ReactElement {
  const [view, setView] = useState<ActionItemDetailView>('context');
  // currentData, not data, so switching items shows the spinner rather than the previous item's timeline.
  const { currentData: timeline, isFetching } = useGetVendorActionItemTimelineQuery({ vendorId: item.vendorId, vendorActionItemId: item.id });
  const entries = useMemo(() => (timeline ? timelineView(timeline, 'all') : []), [timeline]);
  const comments = useMemo(() => (timeline ? timelineView(timeline, 'comments') : undefined), [timeline]);
  const canceled = item.status === api.VendorActionItemStatus.CANCELED;

  return (
    <Detail>
      <div>
        <SectionLabel style={{ marginBottom: 4 }}>Description</SectionLabel>
        <InlineText
          key={`description-${item.id}`}
          multiline
          value={item.description ?? ''}
          placeholder={canWrite ? 'Add a description…' : 'No description'}
          readOnly={!canWrite}
          display={(v) => <div style={{ color: '#344054', fontSize: 13, whiteSpace: 'pre-wrap' }}><Linkified text={v} /></div>}
          onSave={(description) => actions.update(item.id, { description: description || null })}
        />
      </div>

      <DetailsBox>
        <SectionLabel style={{ gridColumn: '1 / -1', marginBottom: 2 }}>Details</SectionLabel>
        <FieldLabel>Status</FieldLabel>
        <StatusRow role="group" aria-label="Status" data-cy="action-item-status">
          {[LifecycleStatusOptions, CanceledStatusOptions].map((options) => (
            <StatusSegmented key={options[0].value}>
              {options.map(({ value, label }) => (
                <StatusSegment
                  key={value}
                  type="button"
                  $on={item.status === value}
                  $colorKey={actionItemStatusColorKey(value)}
                  aria-pressed={item.status === value}
                  disabled={!canWrite}
                  onClick={() => { if (value !== item.status) actions.update(item.id, { status: value }); }}
                >
                  {label}
                </StatusSegment>
              ))}
            </StatusSegmented>
          ))}
        </StatusRow>
        <FieldLabel>Assignee</FieldLabel>
        <Dropdown
          options={[...assigneeOptions(operators, me, item.assignedTo), UnassignedOption]}
          selectedOption={item.assignedTo ?? ''}
          disabled={!canWrite}
          position="left"
          showSearch
          dataCy="action-item-assignee"
          width="100%"
          maxHeight={MenuMaxHeight}
          onChange={(assignedTo) => { if (assignedTo !== (item.assignedTo ?? '')) actions.update(item.id, { assignedTo: assignedTo || null }); }}
        />
        <FieldLabel style={{ alignSelf: 'start', paddingTop: 4 }}>Programs</FieldLabel>
        <PartnerProgramList item={item} />
        <FieldLabel>Created</FieldLabel>
        <ReadOnlyValue title={item.createdAt}>{`${formatDateTime(item.createdAt)} · ${formatAssigneeEmail(item.createdBy)}`}</ReadOnlyValue>
        <FieldLabel>Updated</FieldLabel>
        <ReadOnlyValue title={item.lastActivityAt}>{`${formatDateTime(item.lastActivityAt)} · ${formatAssignee(item.updatedBy)}`}</ReadOnlyValue>
        {item.closedAt && (
          <>
            <FieldLabel>{canceled ? 'Canceled' : 'Completed'}</FieldLabel>
            <ReadOnlyValue title={item.closedAt}>{formatDateTime(item.closedAt)}</ReadOnlyValue>
          </>
        )}
      </DetailsBox>

      {actions.error && <ErrorText role="alert" onClick={actions.clearError}>{actions.error}</ErrorText>}

      <div>
        <Segmented role="group" aria-label="Activity view">
          {ActionItemDetailViewOptions.map(({ value, label }) => (
            <Segment key={value} type="button" $on={view === value} aria-pressed={view === value} onClick={() => setView(value)}>
              {label}
            </Segment>
          ))}
        </Segmented>
      </div>

      {view === 'context' && (
        // Keyed so another item opened in the drawer starts with its forms closed and comments collapsed.
        <ActionItemContext
          key={item.id}
          item={item}
          comments={comments}
          commentsLoading={isFetching}
          me={me}
          operators={operators}
          canWrite={canWrite}
          actions={actions}
          onOpenItem={onOpenItem}
        />
      )}
      {view === 'vendorSummary' && <VendorSummary vendorId={item.vendorId} />}
      {view === 'all' && (
        <>
          {!timeline && isFetching && <LoadingSpinner height="18px" />}
          {timeline && (entries.length > 0 ? <ActionItemTimeline entries={entries} /> : <Muted>No activity yet</Muted>)}
        </>
      )}
    </Detail>
  );
}
